import { Component, inject, ViewChild } from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { BehaviorSubject, EMPTY } from "rxjs";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";

import { AuthModel, PermissionDeniedError } from "@intocare/auth-data";
import { MFAFailedError, MFATooManyAttemptsError, SessionExpiredError } from "@intocare/aws";

import { MfaLoginFormComponent } from "../../components/mfa-login-form/mfa-login-form.component";

@Component({
	templateUrl: "./mfa.component.html",
	styleUrls: ["./mfa.component.scss"],
	standalone: false,
})
export class MFAComponent {
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private auth = inject(AuthModel);

	@ViewChild(MfaLoginFormComponent, { static: false }) mfaLoginForm: MfaLoginFormComponent;
	loading = false;
	invalidCodeError = false;
	failedMessage: string;

	showFailed$ = new BehaviorSubject(false);
	showMfaLogin$ = new BehaviorSubject(true);

	organisation$ = this.route.paramMap.pipe(
		map((params: ParamMap) => {
			const org = params.get("org");
			const home = params.get("home");

			return home ? `${org}.${home}` : org;
		}),
	);

	onTotpToken({ token }: { token: string }) {
		this.loading = true;

		this.organisation$
			.pipe(
				take(1),
				switchMap((organisation) =>
					this.auth.sendMFAToken(token, organisation).pipe(
						tap(() => {
							window.localStorage.setItem("currentOrganisation", organisation);
							this.router.navigate(["..", "dashboard"], { relativeTo: this.route });
						}),
					),
				),
				catchError((error: unknown) => {
					if (error instanceof MFAFailedError) {
						this.loading = false;
						this.invalidCodeError = true;
						this.mfaLoginForm.clearCode();
						return EMPTY;
					}
					this.failedMessage = "auth.errors.unknown";
					if (error instanceof SessionExpiredError) {
						this.loading = false;
						this.failedMessage = "auth.errors.session";
					}
					if (error instanceof MFATooManyAttemptsError) {
						this.loading = false;
						this.failedMessage = "auth.errors.too_many_attempts";
					}
					if (error instanceof PermissionDeniedError) {
						this.router
							.navigate(["..", "no-permissions"], { relativeTo: this.route })
							.then(() => (this.loading = false))
							.catch(() => (this.loading = false));
						return EMPTY;
					}
					this.showFailed$.next(true);
					this.showMfaLogin$.next(false);
					return EMPTY;
				}),
			)
			.subscribe();
	}

	onChange() {
		this.invalidCodeError = false;
	}

	goToLogin() {
		this.router.navigate(["..", "sign-in-with-username"], { relativeTo: this.route });
	}
}
